import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { projectsAPI } from '../api';
import SEO from '../components/SEO';

function Landing() {
  const { authenticated } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await projectsAPI.list();
      if (response.data.success) {
        const publicProjects = response.data.projects
          .filter(p => p.is_public)
          .slice(0, 6);
        setProjects(publicProjects);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "SharePanda",
    "url": "https://sharepanda.org",
    "description": "The ethical platform for open source creators. Share your projects, set open goals, and connect directly with your community.",
    "applicationCategory": "DeveloperApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "Creator-focused project pages",
      "Open goals and milestones",
      "Direct community support",
      "Transparent funding",
      "No tracking or ads",
      "Open source platform",
      "Static site hosting with CLI"
    ]
  };

  const formatNumber = (num) => {
    if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
    return num;
  };

  const getIconUrl = (iconPath) => {
    if (!iconPath) return null;
    if (iconPath.startsWith('/api/')) {
      const API_URL = process.env.REACT_APP_API_URL || '';
      return API_URL + iconPath.substring(4);
    }
    return iconPath;
  };

  return (
    <>
      <SEO 
        title="SharePanda - The Platform for Open Source Creators"
        description="SharePanda empowers creators to share their projects, host static sites with a Surge.sh-style CLI, set open goals, and connect directly with their community. The ethical, transparent platform for open source developers and indie creators."
        keywords="open source creators, creator platform, open goals, indie developers, project funding, community support, ethical platform, transparent funding, open source projects"
        url="https://sharepanda.org"
        jsonLd={jsonLd}
      />
      <div className="landing-page">
        {/* Hero Section */}
        <section className="hero">
          <div className="container">
            <div className="hero-content">
            <div className="hero-badge">
              <span className="badge badge-success">For Creators, By Creators</span>
            </div>
            <h1 className="hero-title">
              Empower Your Creations.<br />Set Open Goals.
            </h1>
            <p className="hero-subtitle">
              The ethical platform for open source creators. Share your projects, set transparent goals, 
              and build a direct connection with your community—no middlemen, no tracking.
            </p>
            <div className="hero-buttons">
              {authenticated ? (
                <>
                  <Link to="/projects" className="btn btn-primary btn-lg">
                    My Creations
                  </Link>
                  <Link to="/browse" className="btn btn-secondary btn-lg">
                    Discover Creators
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/register" className="btn btn-primary btn-lg">
                    Start Creating
                  </Link>
                  <Link to="/browse" className="btn btn-secondary btn-lg">
                    Discover Creators
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Creators Section */}
      <section className="featured-projects">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Featured Creators</h2>
            <Link to="/browse" className="section-link">
              Explore All →
            </Link>
          </div>

          {loading ? (
            <div className="loading">
              <div className="spinner"></div>
            </div>
          ) : projects.length === 0 ? (
            <div className="empty-projects">
              <h3>Be the first creator!</h3>
              <p>Share your project with the community</p>
              {!authenticated && (
                <Link to="/register" className="btn btn-primary mt-2">
                  Join as Creator
                </Link>
              )}
            </div>
          ) : (
            <div className="project-showcase-grid">
              {projects.map((project) => (
                <Link 
                  key={project.project_id} 
                  to={`/p/${project.slug}`}
                  className="project-showcase-card"
                >
                  <div className="project-showcase-header">
                    {project.thumbnail ? (
                      <img 
                        src={getIconUrl(project.thumbnail)} 
                        alt={project.title}
                        className="project-icon project-icon-img"
                      />
                    ) : (
                      <div className="project-icon">
                        {project.title.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="project-showcase-meta">
                      <h3 className="project-showcase-title">{project.title}</h3>
                      {project.short_description && (
                        <p className="project-showcase-desc">
                          {project.short_description}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="project-showcase-stats">
                    <div className="stat-item">
                      <span className="stat-text">
                        {formatNumber(project.stats.total_downloads)} downloads
                      </span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-text">
                        {project.stats.backers_count || 0} backers
                      </span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-text">
                        {project.downloads.length} {project.downloads.length === 1 ? 'file' : 'files'}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <h2 className="section-title">Built for Creators</h2>
          <p className="section-subtitle">
            Everything you need to share your work and connect with your community
          </p>
          
          <div className="features-grid">
            <div className="feature-card">
              <h3>Open Goals</h3>
              <p>
                Set transparent milestones for your project. Let your community see your roadmap and support your vision.
              </p>
            </div>

            <div className="feature-card">
              <h3>Creator Profiles</h3>
              <p>
                Showcase all your projects in one place. Build your identity and grow your audience organically.
              </p>
            </div>

            <div className="feature-card">
              <h3>Community Support</h3>
              <p>
                Connect directly with backers who believe in your work. No algorithms, no gatekeepers.
              </p>
            </div>

            <div className="feature-card">
              <h3>Transparent & Ethical</h3>
              <p>
                No tracking, no ads, no hidden fees. Your community supports you directly.
              </p>
            </div>

            <div className="feature-card">
              <h3>Share Your Work</h3>
              <p>
                Distribute your software, assets, or creative work. Perfect for indie devs and open source projects.
              </p>
            </div>

            <div className="feature-card">
              <h3>Static Site Hosting</h3>
              <p>
                Deploy websites straight from your terminal with the SharePanda CLI. Get a free <code>&lt;project&gt;.sharepanda.org</code> domain, add your own custom domain, and rollback releases instantly.
              </p>
              <Link to="/static-sites" className="feature-link">
                Learn more →
              </Link>
            </div>

            <div className="feature-card">
              <h3>100% Open Source</h3>
              <p>
                Self-host or use our platform. Your data, your rules. Forever free for creators.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {!authenticated && (
        <section className="cta-section">
          <div className="container">
            <div className="cta-content">
              <h2 className="cta-title">Ready to share your creations?</h2>
              <p className="cta-subtitle">
                Join creators who are building openly and connecting directly with their community
              </p>
              <div className="cta-buttons">
                <Link to="/register" className="btn btn-primary btn-lg">
                  Join as Creator
                </Link>
                <Link to="/browse" className="btn btn-secondary btn-lg">
                  Discover Creators
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
    </>
  );
}

export default Landing;
